import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProspectCatalogs } from "../../../services/v2/prospects.service";
import MarcadorDock, { useMarcador } from "./marcadorDock";
import { TelefonoBoton } from "./telefono/telefonoUI";
import WappIcono from "../../../components/wappIcono";
import { useContadores } from "../../../appMovil/contadores";

export type VentasHerr = "marcador" | "cartera" | "campanas" | "misventas";

interface Props {
	activo: "crm" | "msj" | "team";
	campusId: number | null;
	veCrm: boolean;
	veWappchat: boolean;
	veWappteam: boolean;
	onCrm: () => void;
	onWappchat: () => void;
	onWappteam: () => void;
	/** Sin leer de WappTeam; si no se pasa, el mismo número de la barra de arriba (contadores.ts). */
	nTeam?: number;
	/** Cómo abre el auto-marcador a una persona en esta pantalla. */
	onAbrirMarcador: (id: number) => void;
	catalogs?: ProspectCatalogs | null;
	/** Solo el CRM: la herramienta abierta y cómo cambiarla. En WappChat/WappTeam el menú lleva al CRM con ?herr=. */
	herr?: VentasHerr | null;
	onHerr?: (h: VentasHerr | null) => void;
	onNumeros?: () => void;
	onCampanas?: () => void;
}

/**
 * La barra de CRM · WappChat · WappTeam en un solo lugar (Esteban, 2026-10-18: "los botones se mueven de lugar").
 * Antes cada pantalla tenía su copia: CRM traía marcador + teléfono + Herramientas, WappChat marcador + teléfono y WappTeam
 * solo el teléfono, así que al cambiar de pestaña los botones de la derecha (y en el celular las pestañas) brincaban.
 * Ahora las tres enseñan lo mismo, y Herramientas sale en las tres para Supervisión.
 */
const VentasBarra: React.FC<Props> = (p) => {
	const navigate = useNavigate();
	const m = useMarcador();
	// Supervisión: los catálogos del CRM o los que ya cargó el marcador (sin pedirlos otra vez).
	const contadores = useContadores();
	const nTeam = p.nTeam ?? contadores.wappteam;
	const esSup = !!(p.catalogs || m.catalogs)?.isSupervisor;
	const [menu, setMenu] = useState<boolean>(false);
	const boton = useRef<HTMLButtonElement>(null);
	// El menú va con position: fixed junto al botón: en el celular la barra se desliza de lado (overflow-x) y recortaba el
	// menú, por eso "el botón de cuadritos no hacía nada".
	const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
	useLayoutEffect(() => {
		if (!menu) return;
		const medir = () => {
			const r = boton.current?.getBoundingClientRect();
			if (r) setPos({ top: Math.round(r.bottom + 4), right: Math.max(8, Math.round(window.innerWidth - r.right)) });
		};
		medir();
		window.addEventListener("resize", medir);
		return () => window.removeEventListener("resize", medir);
	}, [menu]);
	useEffect(() => {
		if (!menu) return;
		const cerrar = (e: MouseEvent | TouchEvent) => { if (!(e.target as HTMLElement).closest(".crm-herr")) setMenu(false); };
		document.addEventListener("mousedown", cerrar);
		document.addEventListener("touchstart", cerrar);
		return () => { document.removeEventListener("mousedown", cerrar); document.removeEventListener("touchstart", cerrar); };
	}, [menu]);

	const herr = p.onHerr ? p.herr || null : null;
	const elegir = (h: VentasHerr | null) => (p.onHerr ? p.onHerr(h) : navigate(h ? "/prospects?herr=" + h : "/prospects"));
	const rotacion = () => (p.onNumeros ? p.onNumeros() : navigate("/prospects?herr=numeros"));
	const tab = (k: "crm" | "msj" | "team", texto: string, ir: () => void, n?: number) => (
		<button role="tab" aria-selected={p.activo === k} className={"crm-mod" + (p.activo === k ? " on" : "")} onClick={ir}>
			{texto}{n ? <span className="crm-mod-n">{n.toLocaleString("es-MX")}</span> : null}
		</button>
	);

	return (
		<div className="crm-modulos" role="tablist" aria-label="Ventas">
			{p.veCrm ? tab("crm", "CRM", p.onCrm) : null}
			{p.veWappchat ? tab("msj", "WappChat", p.onWappchat) : null}
			{p.veWappteam ? tab("team", "WappTeam", p.onWappteam, nTeam) : null}
			{p.campusId && p.veCrm ? (
				<span className="crm-mod-der">
					{/* Auto-marcador (Esteban, 2026-10-13): para todas las asesoras, junto al teléfono. */}
					<MarcadorDock campusId={p.campusId} catalogs={p.catalogs} onAbrir={p.onAbrirMarcador} onCampanas={p.onCampanas} />
					<TelefonoBoton campusId={p.campusId} compacto />
					{/* «Mis ventas» (Esteban, 2026-10-30): cada asesora ve lo suyo; Supervisión lo tiene en Herramientas. */}
					{!esSup ? <button className={"btn-wa" + (herr === "misventas" ? " on" : "")} onClick={() => elegir(herr === "misventas" ? null : "misventas")}
						title="Mis ventas">Mis ventas</button> : null}
					{esSup ? <span className="crm-herr cv-menu">
						{/* En el teléfono solo el ícono (Esteban, 2026-10-12: a 390 px "Herramientas" se salía de la barra). */}
						<button ref={boton} className={"btn-wa" + (herr ? " on" : "")} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}
							aria-label="Herramientas" title="Herramientas">
							<span className="crm-herr-ico" aria-hidden="true"><WappIcono n="grid" /></span>
							<span className="crm-herr-txt">{herr === "marcador" ? "Llamar Campaña" : herr === "cartera" ? "Cartera" : herr === "campanas" ? "Campañas" : herr === "misventas" ? "Mis ventas" : "Herramientas"} ▾</span></button>
						{menu ? (
							<div className="cv-menu-lista" role="menu" style={pos ? { position: "fixed", top: pos.top, right: pos.right, left: "auto", zIndex: 1040 } : { visibility: "hidden" }}
								onClick={() => setMenu(false)}>
								{herr ? <button role="menuitem" onClick={() => elegir(null)}><b>← Volver a la lista</b></button> : null}
								<button role="menuitem" onClick={() => elegir("cartera")}><b>Cartera</b><span>Tabla por asesora y reasignar en bloque</span></button>
								<button role="menuitem" onClick={() => elegir("misventas")}><b>Mis ventas</b><span>Solo lo que vendiste tú, desde mayo 2025</span></button>
								<button role="menuitem" onClick={() => elegir("campanas")}><b>Campañas de llamadas</b><span>Listas para el auto-marcador</span></button>
								<button role="menuitem" onClick={() => elegir("marcador")}><b>Llamar una campaña</b><span>La lista diaria ya está en el botón Marcador</span></button>
								<button role="menuitem" onClick={rotacion}><b>Rotación de asesoras</b><span>Quién recibe a quien escribe por primera vez</span></button>
							</div>
						) : null}
					</span> : null}
				</span>
			) : null}
		</div>
	);
};

export default VentasBarra;
